import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Box,
    Card,
    CardContent,
    Grid,
    IconButton,
    Collapse,
    Tooltip,
    Avatar,
    Stack,
    Divider,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemText,
    OutlinedInput,
    useTheme,
    useMediaQuery,
    CircularProgress,
    FormGroup,
    FormControlLabel,
    LinearProgress,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Download,
    Refresh,
    Edit,
    Delete,
    ExpandMore,
    ExpandLess,
    Person,
    Business,
    Email,
    Phone,
    Public,
    AttachMoney,
    CalendarToday,
    Flag,
    CheckCircle,
    Cancel,
    Pending,
    Update,
    SwapHoriz,
    Receipt as ReceiptIcon,
} from '@mui/icons-material';
import CurrenciesWidget from '../widgets/CurrenciesWidget';

const API_URL = process.env.REACT_APP_API_URL;
const SERVICES = [
    "Visa Application",
    "Ticketing",
    "Team Building",
    "Tours",
    "Safaris",
    "Conference Planning",
    "Hotel Reservation",
    'Airport Transfer',
    'Hotel Booking',
    'Safari Tours',
    'City Tour',
    'Car Rental',
    'Flight Booking',
    'Train Booking',
    'Cruise Booking',
    'Visa Assistance',
    'Travel Insurance',
    'Conference Services',
    'Wedding Planning',
    'Honeymoon Package',
    'Group Tours',
    'Corporate Travel'
].sort();

const BASE_CURRENCIES = ['USD', 'EUR', 'GBP', 'KES', 'CNY', 'JPY'];

const STATUS_COLORS = {
    'Pending': '#FF9800',
    'Yes': '#4CAF50',
    'No': '#F44336'
};

const STATUS_ICONS = {
    'Pending': <Pending />,
    'Yes': <CheckCircle />,
    'No': <Cancel />
};

export default function ClientsDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [clients, setClients] = useState([]);
    const [groupedClients, setGroupedClients] = useState({});
    const [loading, setLoading] = useState(true);
    const [expandedClients, setExpandedClients] = useState({});
    const [expandedServices, setExpandedServices] = useState({});
    const [editingClient, setEditingClient] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedServices, setSelectedServices] = useState([]);
    const [editFormData, setEditFormData] = useState({
        quotation_amount: '',
        currency: 'KES',
        destination: '',
        date_from: '',
        date_to: '',
        conversion_status: 'Pending',
        exchange_rate: '',
        base_currency: 'USD',
        package_name: '',
    });
    const [updatingServiceId, setUpdatingServiceId] = useState(null);
    const [deletingServiceId, setDeletingServiceId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [fetchingRate, setFetchingRate] = useState(false);
    const [rateLastUpdated, setRateLastUpdated] = useState(null);
    const [packageNameError, setPackageNameError] = useState(false);
    const [expenses, setExpenses] = useState([]);
    const [expensesByService, setExpensesByService] = useState({});
    const [displayCurrency, setDisplayCurrency] = useState('USD');
    const token = localStorage.getItem("token");

    const packageOptions = [
        "Coast Package",
        "Safari Package",
        "Hotel Package",
        "Standard Package",
        "Premium Package",
        "Deluxe Package",
        "Executive Package",
        "Custom Package",
        "Group Package",
        "Corporate Package",
        "Family Package",
        "Adventure Package",
        "Luxury Package",
        "Economy Package",
        "Business Package"
    ];

    const fetchClients = async () => {
        setLoading(true);
        try {
            const clientsRes = await axios.get(`${API_URL}/clients`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setClients(clientsRes.data);

            const expensesRes = await axios.get(`${API_URL}/api/expenses/user/1/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const expensesData = expensesRes.data.data?.expenses || [];
            setExpenses(expensesData);

            const grouped = {};
            expensesData.forEach(exp => {
                const key = exp.client_transaction_id;
                if (key) {
                    const numericKey = typeof key === 'string' ? parseInt(key) : key;
                    if (!grouped[numericKey]) grouped[numericKey] = [];
                    grouped[numericKey].push(exp);
                }
            });

            setExpensesByService(grouped);
            groupClientsByCustomer(clientsRes.data);
        } catch (err) {
            console.error('Error fetching data:', err);
            showSnackbar('Failed to fetch data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const groupClientsByCustomer = (data) => {
        const grouped = data.reduce((acc, client) => {
            const key = client.customer_id;
            if (!acc[key]) {
                acc[key] = {
                    customer_id: client.customer_id,
                    contact_name: client.contact_name,
                    company_name: client.company_name,
                    email: client.email,
                    phone_number: client.phone_number,
                    nationality: client.nationality,
                    services: []
                };
            }
            acc[key].services.push({
                service_id: client.service_id,
                nature_of_service: client.nature_of_service,
                package_name: client.package_name || '',
                currency: client.currency,
                quotation_amount: client.quotation_amount,
                destination: client.destination,
                booking_date: client.booking_date,
                date_from: client.date_from,
                date_to: client.date_to,
                conversion_status: client.conversion_status,
                exchange_rate: client.exchange_rate || '',
                base_currency: client.base_currency || 'USD',
            });
            return acc;
        }, {});
        setGroupedClients(grouped);

        const expanded = {};
        Object.keys(grouped).forEach(key => {
            expanded[key] = false;
        });
        setExpandedClients(expanded);
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ open: false, message: '', severity: 'success' });
    };

    const toggleExpand = (customerId) => {
        setExpandedClients(prev => ({
            ...prev,
            [customerId]: !prev[customerId]
        }));
    };

    const toggleServiceExpand = (serviceId) => {
        setExpandedServices(prev => ({
            ...prev,
            [serviceId]: !prev[serviceId]
        }));
    };

    const formatDate = (isoString) => {
        if (!isoString) return "";
        const d = new Date(isoString);
        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateForInput = (isoString) => {
        if (!isoString) return "";
        const d = new Date(isoString);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const formatCurrency = (amount, currency = 'KES') => {
        if (amount === undefined || amount === null || isNaN(amount)) return '0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'KES',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const calculateServiceProfit = (service) => {
        const amount = parseFloat(service.quotation_amount) || 0;
        const fromCurrency = service.currency || 'KES';
        const baseCurrency = service.base_currency || 'USD';
        const exchangeRate = service.exchange_rate ? parseFloat(service.exchange_rate) : null;

        const serviceId = service.service_id;
        const serviceExpenses = expensesByService[serviceId] ||
            expensesByService[String(serviceId)] ||
            expensesByService[Number(serviceId)] || [];

        // Calculate total expenses in KES
        let totalExpensesKES = 0;
        serviceExpenses.forEach(exp => {
            const expAmount = parseFloat(exp.amount) || 0;
            const expCurrency = exp.currency || 'KES';

            if (expCurrency === 'KES') {
                totalExpensesKES += expAmount;
            } else if (expCurrency === 'USD') {
                if (exchangeRate && exchangeRate > 0) {
                    if (exchangeRate > 1) {
                        totalExpensesKES += expAmount * exchangeRate;
                    } else {
                        totalExpensesKES += expAmount / exchangeRate;
                    }
                } else {
                    totalExpensesKES += expAmount * 150;
                }
            } else {
                totalExpensesKES += expAmount;
            }
        });

        // Convert revenue to KES
        let revenueKES = amount;
        if (fromCurrency === 'USD') {
            if (exchangeRate && exchangeRate > 0) {
                if (exchangeRate > 1) {
                    revenueKES = amount * exchangeRate;
                } else {
                    revenueKES = amount / exchangeRate;
                }
            } else {
                revenueKES = amount * 150;
            }
        } else if (fromCurrency === 'KES') {
            revenueKES = amount;
        }

        // Calculate in USD
        let totalExpensesUSD = 0;
        let revenueUSD = 0;

        if (exchangeRate && exchangeRate > 0) {
            if (exchangeRate > 1) {
                totalExpensesUSD = totalExpensesKES / exchangeRate;
                revenueUSD = revenueKES / exchangeRate;
            } else {
                totalExpensesUSD = totalExpensesKES * exchangeRate;
                revenueUSD = revenueKES * exchangeRate;
            }
        } else {
            totalExpensesUSD = totalExpensesKES / 150;
            revenueUSD = revenueKES / 150;
        }

        const profitKES = revenueKES - totalExpensesKES;
        const profitUSD = revenueUSD - totalExpensesUSD;
        const profitMarginKES = revenueKES > 0 ? (profitKES / revenueKES) * 100 : 0;
        const profitMarginUSD = revenueUSD > 0 ? (profitUSD / revenueUSD) * 100 : 0;

        return {
            revenueKES: revenueKES || 0,
            revenueUSD: revenueUSD || 0,
            expensesKES: totalExpensesKES || 0,
            expensesUSD: totalExpensesUSD || 0,
            profitKES: profitKES || 0,
            profitUSD: profitUSD || 0,
            profitMarginKES: profitMarginKES || 0,
            profitMarginUSD: profitMarginUSD || 0,
            expenseCount: serviceExpenses.length || 0,
            expenses: serviceExpenses || []
        };
    };

    const getDisplayValues = (profitData, currency) => {
        if (currency === 'USD') {
            return {
                revenue: profitData.revenueUSD,
                expenses: profitData.expensesUSD,
                profit: profitData.profitUSD,
                profitMargin: profitData.profitMarginUSD,
                currencyCode: 'USD'
            };
        } else {
            return {
                revenue: profitData.revenueKES,
                expenses: profitData.expensesKES,
                profit: profitData.profitKES,
                profitMargin: profitData.profitMarginKES,
                currencyCode: 'KES'
            };
        }
    };

    const getTotalConvertedValue = (services, targetCurrency) => {
        let total = 0;
        services.forEach(service => {
            const amount = parseFloat(service.quotation_amount) || 0;
            const fromCurrency = service.currency || 'KES';
            const exchangeRate = service.exchange_rate;
            const baseCurrency = service.base_currency || 'USD';

            let convertedAmount = 0;

            if (fromCurrency === targetCurrency) {
                convertedAmount = amount;
            } else if (baseCurrency === targetCurrency && exchangeRate) {
                let rate = parseFloat(exchangeRate);
                if (fromCurrency === 'USD' && rate < 1) {
                    rate = 1 / rate;
                }
                if (fromCurrency === 'KES' && rate > 1) {
                    rate = 1 / rate;
                }
                convertedAmount = amount * rate;
            } else if (fromCurrency === targetCurrency) {
                convertedAmount = amount;
            } else if (exchangeRate) {
                let rate = parseFloat(exchangeRate);
                if (fromCurrency === 'USD' && targetCurrency === 'KES' && rate < 1) {
                    rate = 1 / rate;
                }
                if (fromCurrency === 'KES' && targetCurrency === 'USD' && rate > 1) {
                    rate = 1 / rate;
                }
                convertedAmount = amount * rate;
            } else {
                convertedAmount = amount;
            }
            total += convertedAmount;
        });
        return total;
    };

    const getTotalsByCurrency = (services) => {
        const totals = {};
        services.forEach(service => {
            const currency = service.currency || 'KES';
            const amount = parseFloat(service.quotation_amount) || 0;
            totals[currency] = (totals[currency] || 0) + amount;
        });
        return totals;
    };

    const fetchExchangeRate = async (sourceCurrency, targetCurrency) => {
        if (sourceCurrency === targetCurrency) {
            return "1.0000";
        }

        setFetchingRate(true);
        try {
            const response = await axios.get(
                `https://api.exchangerate-api.com/v4/latest/${sourceCurrency}`
            );
            const rate = response.data.rates[targetCurrency];
            if (rate) {
                setRateLastUpdated(new Date());
                return rate.toFixed(4);
            }
        } catch (error) {
            console.error("Error fetching exchange rate from primary API:", error);
            try {
                const fallbackResponse = await axios.get(
                    `https://api.frankfurter.app/latest?from=${sourceCurrency}&to=${targetCurrency}`
                );
                const rate = fallbackResponse.data.rates[targetCurrency];
                if (rate) {
                    setRateLastUpdated(new Date());
                    return rate.toFixed(4);
                }
            } catch (fallbackError) {
                console.error("Fallback API also failed:", fallbackError);
                showSnackbar('Failed to fetch exchange rate', 'warning');
            }
        } finally {
            setFetchingRate(false);
        }
        return '';
    };

    const handleFetchRate = async () => {
        if (!editFormData.currency || !editFormData.base_currency) {
            showSnackbar('Please select both source and target currencies', 'warning');
            return;
        }

        const rate = await fetchExchangeRate(editFormData.currency, editFormData.base_currency);
        if (rate) {
            setEditFormData(prev => ({ ...prev, exchange_rate: rate }));
            showSnackbar(`Exchange rate updated: 1 ${editFormData.currency} = ${rate} ${editFormData.base_currency}`, 'success');
        }
    };

    const handleSaveService = async (serviceId, updates) => {
        setUpdatingServiceId(serviceId);

        try {
            updateServiceInUI(serviceId, updates);

            await axios.put(`${API_URL}/services/${serviceId}`, updates, {
                headers: { Authorization: `Bearer ${token}` }
            });

            showSnackbar('Service updated successfully', 'success');
            setEditDialogOpen(false);
            setEditingClient(null);
            setSelectedServices([]);
            setPackageNameError(false);

        } catch (err) {
            showSnackbar('Update failed. Reverting changes...', 'error');
            await fetchClients();
        } finally {
            setUpdatingServiceId(null);
        }
    };

    const handleDeleteService = async (customerId, serviceId) => {
        if (!window.confirm("Are you sure you want to delete this service?")) return;

        setDeletingServiceId(serviceId);

        try {
            removeServiceFromUI(customerId, serviceId);

            await axios.delete(`${API_URL}/services/${customerId}/service/${serviceId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            showSnackbar('Service deleted successfully', 'success');

        } catch (err) {
            showSnackbar('Delete failed. Reverting...', 'error');
            await fetchClients();
        } finally {
            setDeletingServiceId(null);
        }
    };

    const updateServiceInUI = (serviceId, updates) => {
        const updatedClients = clients.map(client => {
            if (client.service_id === serviceId) {
                return {
                    ...client,
                    ...updates,
                    nature_of_service: updates.nature_of_service || client.nature_of_service,
                    package_name: updates.package_name || client.package_name,
                };
            }
            return client;
        });
        setClients(updatedClients);

        const updatedGrouped = { ...groupedClients };
        Object.keys(updatedGrouped).forEach(customerId => {
            updatedGrouped[customerId].services = updatedGrouped[customerId].services.map(service => {
                if (service.service_id === serviceId) {
                    return {
                        ...service,
                        ...updates,
                        nature_of_service: updates.nature_of_service || service.nature_of_service,
                        package_name: updates.package_name || service.package_name,
                    };
                }
                return service;
            });
        });
        setGroupedClients(updatedGrouped);
    };

    const removeServiceFromUI = (customerId, serviceId) => {
        const updatedClients = clients.filter(client => client.service_id !== serviceId);
        setClients(updatedClients);

        const updatedGrouped = { ...groupedClients };
        if (updatedGrouped[customerId]) {
            updatedGrouped[customerId].services = updatedGrouped[customerId].services.filter(
                service => service.service_id !== serviceId
            );

            if (updatedGrouped[customerId].services.length === 0) {
                delete updatedGrouped[customerId];
            }
        }
        setGroupedClients(updatedGrouped);
    };

    const exportToExcel = () => {
        const data = clients.map((c) => {
            return {
                "Contact Name": c.contact_name,
                "Company Name": c.company_name,
                Email: c.email,
                Phone: c.phone_number,
                Nationality: c.nationality,
                "Package Name": c.package_name || '',
                "Nature of Service": (Array.isArray(c.nature_of_service)
                    ? c.nature_of_service
                    : c.nature_of_service
                        ? [c.nature_of_service]
                        : []
                ).join(', '),
                "Source Currency (Input)": c.currency,
                "Target Currency (Base)": c.base_currency || 'USD',
                "Quotation Amount": c.quotation_amount,
                "Exchange Rate": c.exchange_rate || '',
                Destination: c.destination,
                "Booking Date": formatDate(c.booking_date),
                "Date From": formatDate(c.date_from),
                "Date To": formatDate(c.date_to),
                "Conversion Status": c.conversion_status,
            };
        });
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Clients");
        XLSX.writeFile(wb, "clients.xlsx");
        showSnackbar('Exported successfully', 'success');
    };

    const getServiceCount = (services) => {
        return services.length;
    };

    const getTotalValue = (services) => {
        return services.reduce((sum, s) => sum + (parseFloat(s.quotation_amount) || 0), 0);
    };

    const openEditDialog = (service) => {
        setEditingClient(service);

        let servicesArray = [];
        if (service.nature_of_service) {
            if (Array.isArray(service.nature_of_service)) {
                servicesArray = service.nature_of_service;
            } else if (typeof service.nature_of_service === 'string') {
                try {
                    const parsed = JSON.parse(service.nature_of_service);
                    servicesArray = Array.isArray(parsed) ? parsed : [service.nature_of_service];
                } catch (e) {
                    if (service.nature_of_service.includes(',')) {
                        servicesArray = service.nature_of_service.split(',').map(s => s.trim());
                    } else {
                        servicesArray = [service.nature_of_service];
                    }
                }
            }
        }

        setSelectedServices(servicesArray);
        setPackageNameError(false);

        setEditFormData({
            quotation_amount: service.quotation_amount || '',
            currency: service.currency || 'KES',
            destination: service.destination || '',
            date_from: formatDateForInput(service.date_from),
            date_to: formatDateForInput(service.date_to),
            conversion_status: service.conversion_status || 'Pending',
            exchange_rate: service.exchange_rate || '',
            base_currency: service.base_currency || 'USD',
            package_name: service.package_name || '',
        });

        setEditDialogOpen(true);
    };

    const handleServiceToggle = (service) => {
        setSelectedServices(prev => {
            const newSelection = prev.includes(service)
                ? prev.filter(s => s !== service)
                : [...prev, service];

            if (newSelection.length <= 1) {
                setPackageNameError(false);
            }

            return newSelection;
        });
    };

    const handleFormChange = (field, value) => {
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (field === 'package_name' && value) {
            setPackageNameError(false);
        }
    };

    const handleEditSave = () => {
        if (!editingClient) return;

        if (selectedServices.length > 1 && !editFormData.package_name) {
            setPackageNameError(true);
            showSnackbar('Package Name is required when multiple services are selected', 'warning');
            return;
        }

        const updates = {
            quotation_amount: editFormData.quotation_amount,
            currency: editFormData.currency,
            destination: editFormData.destination,
            date_from: editFormData.date_from,
            date_to: editFormData.date_to,
            conversion_status: editFormData.conversion_status,
            nature_of_service: selectedServices,
            exchange_rate: editFormData.exchange_rate,
            base_currency: editFormData.base_currency,
            package_name: editFormData.package_name,
        };

        handleSaveService(editingClient.service_id, updates);
    };

    const getConvertedAmountDisplay = () => {
        if (!editFormData.quotation_amount || !editFormData.exchange_rate) return '';
        const amount = parseFloat(editFormData.quotation_amount);
        const rate = parseFloat(editFormData.exchange_rate);
        if (isNaN(amount) || isNaN(rate)) return '';
        if (editFormData.currency === editFormData.base_currency) {
            return amount.toFixed(2);
        }
        return (amount * rate).toFixed(2);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 3,
                gap: 2
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                            Clients Dashboard
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {Object.keys(groupedClients).length} clients • {clients.length} services • {expenses.length} expenses
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Display in:
                        </Typography>
                        <ToggleButtonGroup
                            value={displayCurrency}
                            exclusive
                            onChange={(e, newCurrency) => {
                                if (newCurrency !== null) {
                                    setDisplayCurrency(newCurrency);
                                }
                            }}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 2,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    borderColor: theme.palette.divider,
                                    '&.Mui-selected': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                        }
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="USD">$ USD</ToggleButton>
                            <ToggleButton value="KES">KSh KES</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={fetchClients}
                        size={isMobile ? 'small' : 'medium'}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={exportToExcel}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                            background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #45a049 30%, #3d8b40 90%)',
                            }
                        }}
                    >
                        Export Excel
                    </Button>
                </Box>
            </Box>

            {/* Client Cards */}
            <Grid container spacing={3}>
                {Object.values(groupedClients).map((client) => {
                    const isExpanded = expandedClients[client.customer_id] || false;
                    const serviceCount = getServiceCount(client.services);
                    const totalsByCurrency = getTotalsByCurrency(client.services);
                    const totalConverted = getTotalConvertedValue(client.services, 'USD');
                    const hasMultipleCurrencies = Object.keys(totalsByCurrency).length > 1;

                    let totalClientProfitKES = 0;
                    let totalClientExpensesKES = 0;
                    let totalClientRevenueKES = 0;
                    let totalClientProfitUSD = 0;
                    let totalClientExpensesUSD = 0;
                    let totalClientRevenueUSD = 0;

                    client.services.forEach(service => {
                        const profitData = calculateServiceProfit(service);
                        totalClientRevenueKES += profitData.revenueKES;
                        totalClientExpensesKES += profitData.expensesKES;
                        totalClientProfitKES += profitData.profitKES;
                        totalClientRevenueUSD += profitData.revenueUSD;
                        totalClientExpensesUSD += profitData.expensesUSD;
                        totalClientProfitUSD += profitData.profitUSD;
                    });

                    const displayProfit = displayCurrency === 'USD' ? totalClientProfitUSD : totalClientProfitKES;
                    const clientProfitMargin = displayCurrency === 'USD'
                        ? (totalClientRevenueUSD > 0 ? (totalClientProfitUSD / totalClientRevenueUSD) * 100 : 0)
                        : (totalClientRevenueKES > 0 ? (totalClientProfitKES / totalClientRevenueKES) * 100 : 0);

                    return (
                        <Grid item xs={12} key={client.customer_id}>
                            <Card sx={{
                                borderRadius: 2,
                                boxShadow: theme.shadows[2],
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: theme.shadows[4],
                                },
                                overflow: 'hidden',
                            }}>
                                {/* Client Header */}
                                <Box sx={{
                                    p: { xs: 2, md: 3 },
                                    bgcolor: theme.palette.background.default,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    gap: 2,
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                bgcolor: theme.palette.primary.main,
                                                color: 'white',
                                            }}
                                        >
                                            {client.contact_name?.charAt(0) || 'C'}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {client.contact_name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {client.company_name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                                {client.email && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {client.email}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {client.phone_number && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {client.phone_number}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {client.nationality && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Public sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {client.nationality}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexWrap: 'wrap',
                                    }}>
                                        <Chip
                                            label={`${serviceCount} Services`}
                                            color="primary"
                                            size="small"
                                        />
                                        <Chip
                                            label={`Profit: ${formatCurrency(displayProfit, displayCurrency)}`}
                                            size="small"
                                            sx={{
                                                bgcolor: displayProfit >= 0 ? '#4CAF50' : '#F44336',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                        <Chip
                                            label={`Margin: ${clientProfitMargin.toFixed(1)}%`}
                                            size="small"
                                            sx={{
                                                bgcolor: clientProfitMargin >= 20 ? '#4CAF50' :
                                                    clientProfitMargin >= 0 ? '#FF9800' : '#F44336',
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                        {Object.entries(totalsByCurrency).map(([currency, amount]) => (
                                            <Chip
                                                key={currency}
                                                label={`${currency}: ${formatCurrency(amount, currency)}`}
                                                color="secondary"
                                                size="small"
                                                variant="outlined"
                                            />
                                        ))}
                                        {hasMultipleCurrencies && totalConverted > 0 && (
                                            <Chip
                                                label={`≈ ${formatCurrency(totalConverted, 'USD')}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        )}
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => toggleExpand(client.customer_id)}
                                            endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                                        >
                                            {isExpanded ? 'Hide' : 'View'} Services
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Services Table */}
                                <Collapse in={isExpanded}>
                                    <Box sx={{ overflowX: 'auto' }}>
                                        <Table size={isMobile ? 'small' : 'medium'}>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                                    <TableCell>Service</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                    <TableCell align="right">Revenue</TableCell>
                                                    <TableCell align="right">Expenses</TableCell>
                                                    <TableCell align="right">Profit</TableCell>
                                                    <TableCell align="right">Margin</TableCell>
                                                    <TableCell>Destination</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell align="center">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {client.services.map((service) => {
                                                    const isUpdating = updatingServiceId === service.service_id;
                                                    const isDeleting = deletingServiceId === service.service_id;
                                                    const isServiceExpanded = expandedServices[service.service_id] || false;
                                                    const profitData = calculateServiceProfit(service);
                                                    const displayVals = getDisplayValues(profitData, displayCurrency);

                                                    const displayService = service.package_name || service.nature_of_service;

                                                    return (
                                                        <React.Fragment key={service.service_id}>
                                                            <TableRow
                                                                hover
                                                                sx={{
                                                                    opacity: isDeleting ? 0.5 : 1,
                                                                    transition: 'opacity 0.3s ease',
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    {isUpdating && (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                            <LinearProgress sx={{ flex: 1 }} />
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Updating...
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    {displayService && (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {service.package_name ? (
                                                                                <Chip
                                                                                    label={service.package_name}
                                                                                    size="small"
                                                                                    color="primary"
                                                                                    sx={{ fontWeight: 'bold' }}
                                                                                />
                                                                            ) : (
                                                                                Array.isArray(displayService)
                                                                                    ? displayService.map((s, i) => (
                                                                                        <Chip key={i} label={s} size="small" variant="outlined" />
                                                                                    ))
                                                                                    : <Chip label={displayService} size="small" variant="outlined" />
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                    {service.package_name && service.nature_of_service && (
                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                            Services: {Array.isArray(service.nature_of_service)
                                                                                ? service.nature_of_service.join(', ')
                                                                                : service.nature_of_service}
                                                                        </Typography>
                                                                    )}
                                                                    {profitData.expenseCount > 0 && (
                                                                        <Button
                                                                            size="small"
                                                                            startIcon={<ReceiptIcon />}
                                                                            onClick={() => toggleServiceExpand(service.service_id)}
                                                                            sx={{ mt: 0.5 }}
                                                                        >
                                                                            {isServiceExpanded ? 'Hide' : 'View'} {profitData.expenseCount} Expenses
                                                                        </Button>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {formatCurrency(service.quotation_amount, service.currency)}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        {service.currency}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="body2" color="primary" fontWeight="bold">
                                                                        {formatCurrency(displayVals.revenue, displayCurrency)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Box>
                                                                        <Typography variant="body2" color="error">
                                                                            {displayVals.expenses > 0
                                                                                ? formatCurrency(displayVals.expenses, displayCurrency)
                                                                                : formatCurrency(0, displayCurrency)}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                                            {profitData.expenseCount || 0} expenses
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="bold"
                                                                        sx={{ color: displayVals.profit >= 0 ? '#4CAF50' : '#F44336' }}
                                                                    >
                                                                        {formatCurrency(displayVals.profit, displayCurrency)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Chip
                                                                        label={`${displayVals.profitMargin.toFixed(1)}%`}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: displayVals.profitMargin >= 20 ? '#4CAF50' :
                                                                                displayVals.profitMargin >= 0 ? '#FF9800' : '#F44336',
                                                                            color: 'white',
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{service.destination || '-'}</TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={service.conversion_status || 'Pending'}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: STATUS_COLORS[service.conversion_status] + '20',
                                                                            color: STATUS_COLORS[service.conversion_status],
                                                                            fontWeight: 'bold',
                                                                        }}
                                                                        icon={STATUS_ICONS[service.conversion_status]}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                                                        <Tooltip title="Edit">
                                                                            <span>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="primary"
                                                                                    onClick={() => openEditDialog(service)}
                                                                                    disabled={isUpdating || isDeleting}
                                                                                >
                                                                                    <Edit fontSize="small" />
                                                                                </IconButton>
                                                                            </span>
                                                                        </Tooltip>
                                                                        <Tooltip title="Delete">
                                                                            <span>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="error"
                                                                                    onClick={() => handleDeleteService(client.customer_id, service.service_id)}
                                                                                    disabled={isUpdating || isDeleting}
                                                                                >
                                                                                    {isDeleting ? (
                                                                                        <CircularProgress size={20} />
                                                                                    ) : (
                                                                                        <Delete fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            </span>
                                                                        </Tooltip>
                                                                    </Stack>
                                                                </TableCell>
                                                            </TableRow>
                                                            {/* Expenses Sub-table */}
                                                            {isServiceExpanded && profitData.expenses.length > 0 && (
                                                                <TableRow>
                                                                    <TableCell colSpan={9} sx={{ py: 0, bgcolor: theme.palette.grey[50] }}>
                                                                        <Box sx={{ p: 2 }}>
                                                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                                                <ReceiptIcon sx={{ mr: 0.5, fontSize: 16 }} />
                                                                                Linked Expenses
                                                                                (Total: {formatCurrency(displayVals.expenses, displayCurrency)})
                                                                            </Typography>
                                                                            <Table size="small">
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <TableCell>Title</TableCell>
                                                                                        <TableCell>Category</TableCell>
                                                                                        <TableCell align="right">Amount</TableCell>
                                                                                        <TableCell>Date</TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {profitData.expenses.map((exp, idx) => (
                                                                                        <TableRow key={idx}>
                                                                                            <TableCell>{exp.title || 'N/A'}</TableCell>
                                                                                            <TableCell>{exp.category || 'N/A'}</TableCell>
                                                                                            <TableCell align="right">
                                                                                                {formatCurrency(exp.amount, exp.currency || 'KES')}
                                                                                            </TableCell>
                                                                                            <TableCell>{formatDate(exp.date)}</TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                    {/* Total row */}
                                                                                    <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                                                                        <TableCell colSpan={2}>
                                                                                            <Typography variant="subtitle2" fontWeight="bold">
                                                                                                Total Expenses
                                                                                            </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            <Typography variant="subtitle2" fontWeight="bold" color="error">
                                                                                                {formatCurrency(displayVals.expenses, displayCurrency)}
                                                                                            </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell></TableCell>
                                                                                    </TableRow>
                                                                                </TableBody>
                                                                            </Table>
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </Collapse>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Edit Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setEditingClient(null);
                    setSelectedServices([]);
                    setPackageNameError(false);
                }}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6">Edit Service</Typography>
                </DialogTitle>
                <DialogContent>
                    {editingClient && (
                        <Box sx={{ mt: 3 }}>
                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel shrink>Nature of Service</InputLabel>
                                <Box sx={{ mt: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                                    <Grid container spacing={1}>
                                        {SERVICES.map((service) => {
                                            const isChecked = selectedServices.includes(service);
                                            return (
                                                <Grid item xs={12} sm={6} md={4} key={service}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isChecked}
                                                                onChange={() => handleServiceToggle(service)}
                                                                color="primary"
                                                            />
                                                        }
                                                        label={service}
                                                    />
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            </FormControl>

                            {selectedServices.length > 1 && (
                                <FormControl fullWidth sx={{ mb: 3 }} error={packageNameError}>
                                    <InputLabel>Package Name *</InputLabel>
                                    <Select
                                        value={editFormData.package_name}
                                        onChange={(e) => handleFormChange('package_name', e.target.value)}
                                        label="Package Name *"
                                    >
                                        <MenuItem value="">Select Package</MenuItem>
                                        {packageOptions.map((pkg, index) => (
                                            <MenuItem key={index} value={pkg}>
                                                {pkg}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {packageNameError && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            Package Name is required when multiple services are selected
                                        </Typography>
                                    )}
                                </FormControl>
                            )}

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Quotation Amount"
                                        type="number"
                                        value={editFormData.quotation_amount}
                                        onChange={(e) => handleFormChange('quotation_amount', e.target.value)}
                                        InputProps={{
                                            startAdornment: <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CurrenciesWidget
                                        selectedCurrency={editFormData.currency}
                                        onCurrencyChange={(currency) => handleFormChange('currency', currency)}
                                        label="Source Currency (Input)"
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        The currency you're inputting the amount in
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Destination"
                                        value={editFormData.destination}
                                        onChange={(e) => handleFormChange('destination', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date From"
                                        type="date"
                                        value={editFormData.date_from}
                                        onChange={(e) => handleFormChange('date_from', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            startAdornment: <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date To"
                                        type="date"
                                        value={editFormData.date_to}
                                        onChange={(e) => handleFormChange('date_to', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            startAdornment: <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                    />
                                </Grid>

                                {/* Exchange Rate Section */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Exchange Rate & Conversion" size="small" icon={<SwapHoriz />} />
                                    </Divider>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Target Currency (Base)</InputLabel>
                                        <Select
                                            value={editFormData.base_currency}
                                            onChange={(e) => handleFormChange('base_currency', e.target.value)}
                                            label="Target Currency (Base)"
                                        >
                                            {BASE_CURRENCIES.map((currency) => (
                                                <MenuItem key={currency} value={currency}>
                                                    {currency}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary">
                                        The currency you're converting to
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <TextField
                                            fullWidth
                                            label="Exchange Rate"
                                            type="number"
                                            step="0.0001"
                                            value={editFormData.exchange_rate}
                                            onChange={(e) => handleFormChange('exchange_rate', e.target.value)}
                                            placeholder="e.g., 150.50"
                                            helperText={`1 ${editFormData.currency} = ? ${editFormData.base_currency}`}
                                            InputProps={{
                                                startAdornment: <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />
                                            }}
                                        />
                                        <Tooltip title="Fetch current exchange rate from internet">
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={handleFetchRate}
                                                disabled={fetchingRate}
                                                sx={{
                                                    minWidth: 'auto',
                                                    mt: 1,
                                                    height: '56px'
                                                }}
                                            >
                                                {fetchingRate ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                    <Update />
                                                )}
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                    {rateLastUpdated && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            Last updated: {rateLastUpdated.toLocaleTimeString()}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label={`Converted Amount (${editFormData.base_currency})`}
                                        type="text"
                                        value={getConvertedAmountDisplay()}
                                        disabled
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                backgroundColor: '#f5f5f5',
                                            }
                                        }}
                                        helperText={
                                            editFormData.currency !== editFormData.base_currency
                                                ? `${editFormData.quotation_amount || 0} ${editFormData.currency} = ${getConvertedAmountDisplay() || 0} ${editFormData.base_currency}`
                                                : 'Same currency, no conversion needed'
                                        }
                                        InputProps={{
                                            startAdornment: <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }}>
                                        <Chip label="Status" size="small" />
                                    </Divider>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Conversion Status</InputLabel>
                                        <Select
                                            value={editFormData.conversion_status}
                                            onChange={(e) => handleFormChange('conversion_status', e.target.value)}
                                            label="Conversion Status"
                                        >
                                            <MenuItem value="Pending">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Pending sx={{ color: '#FF9800' }} />
                                                    Pending
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Yes">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CheckCircle sx={{ color: '#4CAF50' }} />
                                                    Yes
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="No">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Cancel sx={{ color: '#F44336' }} />
                                                    No
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Button
                        onClick={() => {
                            setEditDialogOpen(false);
                            setEditingClient(null);
                            setSelectedServices([]);
                            setPackageNameError(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleEditSave}
                        disabled={updatingServiceId !== null}
                    >
                        {updatingServiceId ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}